/**
 * Screen shake and the CRT scanline overlay.
 */

/** Exponential-decay screen shake. Add trauma on impacts; sample an offset each frame. */
export class ScreenShake {
  private trauma = 0;
  /** Higher = faster recovery. */
  private readonly decay: number;
  private readonly maxOffset: number;

  constructor(maxOffset = 14, decay = 2.4) {
    this.maxOffset = maxOffset;
    this.decay = decay;
  }

  /** `amount` in [0,1]; stacks, clamped. */
  add(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /** Advance by `dt` seconds and return the pixel offset for this frame. */
  sample(dt: number): { x: number; y: number } {
    if (this.trauma <= 0) return { x: 0, y: 0 };
    const shake = this.trauma * this.trauma; // ease-out
    this.trauma = Math.max(0, this.trauma - this.decay * dt);
    return {
      x: (Math.random() * 2 - 1) * this.maxOffset * shake,
      y: (Math.random() * 2 - 1) * this.maxOffset * shake,
    };
  }

  reset(): void {
    this.trauma = 0;
  }
}

/** Draws horizontal scanlines + a vignette over the whole canvas. */
export function drawScanlines(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  ctx.save();
  ctx.globalAlpha = 0.22;
  ctx.fillStyle = "#000000";
  for (let y = 0; y < height; y += 3) {
    ctx.fillRect(0, y, width, 1);
  }
  ctx.globalAlpha = 1;
  const vignette = ctx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.3,
    width / 2,
    height / 2,
    Math.max(width, height) * 0.75,
  );
  vignette.addColorStop(0, "rgba(0,0,0,0)");
  vignette.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}
